// Enhanced Analytics Service with OpenReplay Session Replay Integration
import { secureRandomString } from "@/utils/secureRandom";
import { initGoogleAnalytics, trackGaPageView } from "@/lib/googleAnalytics";
// This service handles user identification and session replay tracking

interface OpenReplayStartOptions {
  projectKey?: string;
  obscureInputEmails?: boolean;
  obscureTextEmails?: boolean;
  defaultInputMode?: number;
  obscureTextSelector?: string;
  [key: string]: unknown;
}

interface OpenReplayEventPayload {
  [key: string]: unknown;
}

declare global {
  interface Window {
    OpenReplay: {
      setUserID: (id: string) => void;
      setUserAnonymousID: (id: string) => void;
      setMetadata: (key: string, value: string) => void;
      event: (name: string, payload?: OpenReplayEventPayload, issue?: boolean) => void;
      issue: (name: string, payload?: OpenReplayEventPayload) => void;
      start: (opts?: OpenReplayStartOptions) => void;
      stop: () => void;
      getSessionURL: () => string | null;
      isActive: () => boolean;
    };
  }
}

interface UserData {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  [key: string]: unknown;
}

function browserWindow(): Window | undefined {
  return globalThis.window ?? undefined;
}

function stringifyUserId(userId: unknown): string | null {
  if (typeof userId === "string") {
    return userId;
  }
  if (typeof userId === "number" && Number.isFinite(userId)) {
    return String(userId);
  }
  return null;
}

class AnalyticsTracker {
  private isInitialized = false;
  private currentUser: UserData | null = null;
  private errorCount = 0;
  private readonly MAX_ERRORS = 5;

  private getOpenReplay(): Window["OpenReplay"] | undefined {
    const openReplay = browserWindow()?.OpenReplay;
    if (openReplay && typeof openReplay.setUserID === "function") {
      return openReplay;
    }
    return undefined;
  }

  /**
   * Apply Open Replay User Metadata.
   */
  private applyOpenReplayUserMetadata(userData: UserData): void {
    const openReplay = this.getOpenReplay();
    if (typeof openReplay?.setMetadata !== "function") {
      return;
    }

    if (userData.email) {
      openReplay.setMetadata("email", userData.email);
    }
    if (userData.name) {
      openReplay.setMetadata("name", userData.name);
    }
    if (userData.role) {
      openReplay.setMetadata("role", userData.role);
    }
  }

  private isInvalidUserId(userId: string | undefined | null): boolean {
    return !userId || userId === "undefined" || userId === "null";
  }

  private isExplicitlyInvalidUserId(userId: string | undefined): boolean {
    return userId === "undefined" || userId === "null";
  }

  /**
   * Initialize the analytics tracker
   */
  init() {
    if (globalThis.window === undefined) {
      console.warn("Analytics: Window not available, skipping initialization");
      return;
    }

    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }

    // Initialize immediately without waiting for OpenReplay
    this.isInitialized = true;

    initGoogleAnalytics();

    // Try to connect to OpenReplay if available, but don't block
    this.waitForOpenReplay();
  }

  /**
   * Wait for OpenReplay to be available and then initialize
   */
  private waitForOpenReplay() {
    const maxAttempts = 50; // 5 seconds max wait
    let attempts = 0;

    /**
     * Check Open Replay.
     */
    const checkOpenReplay = () => {
      // Vitest/jsdom may tear down `window` while timers are still pending.
      // Guard to avoid "window is not defined" unhandled errors after teardown.
      const win = browserWindow();
      if (!win) {
        return;
      }

      attempts += 1;

      if (this.getOpenReplay()) {
        return;
      }

      if (attempts >= maxAttempts) {
        // OpenReplay is optional/dormant — continue without session replay.
        return;
      }

      // Check again in 100ms
      setTimeout(checkOpenReplay, 100);
    };

    checkOpenReplay();
  }

  /**
   * Identify user in session replay and analytics
   */
  identifyUser(userData: UserData) {
    if (!this.isInitialized) {
      console.warn("Analytics: Cannot identify user - not initialized");
      return;
    }

    const openReplay = this.getOpenReplay();
    if (!openReplay) {
      return;
    }

    // Safety check: ensure userData has a valid ID
    if (!userData || this.isInvalidUserId(userData.id)) {
      console.warn("Analytics: Cannot identify user - invalid user ID:", userData?.id);
      return;
    }

    try {
      this.currentUser = userData;

      if (typeof openReplay.setUserID === "function") {
        openReplay.setUserID(userData.id);
      }

      this.applyOpenReplayUserMetadata(userData);

      // Track user identification event
      this.trackEvent("user_identified", {
        userId: userData.id,
        email: userData.email,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.warn("Failed to identify user in analytics:", error);
      this.handleError(error as Error, { context: "identify_user", userData });
    }
  }

  /**
   * Clear user identification (for logout)
   */
  clearUser() {
    const openReplay = this.getOpenReplay();
    if (!this.isInitialized || !openReplay) return;

    try {
      // Set anonymous ID for session replay
      const anonymousId = `anon_${Date.now()}_${secureRandomString(9)}`;
      openReplay.setUserAnonymousID(anonymousId);

      // Clear current user
      this.currentUser = null;

      // Track logout event
      this.trackEvent("user_logout", {
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.warn("Failed to clear user from analytics:", error);
      this.handleError(error as Error, { context: "clear_user" });
    }
  }

  /**
   * Set user metadata
   */
  setUserMetadata(metadata: Record<string, string>) {
    const openReplay = this.getOpenReplay();
    if (!this.isInitialized || !openReplay) return;

    try {
      Object.entries(metadata).forEach(([key, value]) => {
        openReplay.setMetadata(key, value);
      });
    } catch (error) {
      console.warn("Failed to set user metadata:", error);
      this.handleError(error as Error, { context: "set_metadata", metadata });
    }
  }

  /**
   * Track custom events
   */
  trackEvent(eventName: string, payload?: Record<string, unknown>) {
    if (!this.isInitialized) {
      console.warn("Analytics: Cannot track event - not initialized");
      return;
    }

    const openReplay = this.getOpenReplay();
    if (!openReplay) {
      return;
    }

    // Safety check: ensure we don't track events with undefined user IDs
    const payloadUserId = payload?.userId == null ? null : stringifyUserId(payload.userId);
    if (payloadUserId && this.isInvalidUserId(payloadUserId)) {
      console.warn("Analytics: Skipping event tracking - invalid user ID:", payload.userId);
      return;
    }

    try {
      // Check if event method exists before calling
      if (typeof openReplay.event === "function") {
        openReplay.event(eventName, payload);
      }
    } catch (error) {
      console.warn("Failed to track event:", error);
      this.handleError(error as Error, { context: "track_event", eventName, payload });
    }
  }

  /**
   * Track page views
   */
  trackPageView(pageName: string, additionalData?: Record<string, unknown>) {
    trackGaPageView(pageName, typeof document === "undefined" ? undefined : document.title);

    // Safety check: ensure we don't track with undefined user IDs
    const userId = this.currentUser?.id;
    if (this.isExplicitlyInvalidUserId(userId)) {
      console.warn("Analytics: Skipping page view tracking - invalid user ID:", userId);
      return;
    }

    this.trackEvent("page_view", {
      page: pageName,
      url: browserWindow()?.location.href ?? "",
      timestamp: new Date().toISOString(),
      userId: userId,
      ...additionalData,
    });
  }

  /**
   * Track user actions
   */
  trackUserAction(action: string, details?: Record<string, unknown>) {
    // Safety check: ensure we don't track with undefined user IDs
    const userId = this.currentUser?.id;
    if (this.isExplicitlyInvalidUserId(userId)) {
      console.warn("Analytics: Skipping user action tracking - invalid user ID:", userId);
      return;
    }

    this.trackEvent("user_action", {
      action,
      details,
      userId: userId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track API calls
   */
  trackAPICall(endpoint: string, method: string, status: number, duration?: number) {
    // Only track successful API calls to prevent noise
    if (status >= 200 && status < 300) {
      this.trackEvent("api_call", {
        endpoint,
        method,
        status,
        duration,
        userId: this.currentUser?.id,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric: string, value: number, context?: Record<string, unknown>) {
    this.trackEvent("performance", {
      metric,
      value,
      context,
      userId: this.currentUser?.id,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track authentication events
   */
  trackAuthEvent(
    eventType: "login" | "logout" | "signup" | "password_reset",
    details?: Record<string, unknown>
  ) {
    this.trackEvent("auth_event", {
      type: eventType,
      userId: this.currentUser?.id,
      timestamp: new Date().toISOString(),
      ...details,
    });
  }

  /**
   * Get current user data
   */
  getCurrentUser(): UserData | null {
    return this.currentUser;
  }

  /**
   * Check if user is identified
   */
  isUserIdentified(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Handle errors gracefully
   */
  handleError(error: Error, context?: Record<string, unknown>) {
    // Silently log the error without breaking the app
    console.warn("Analytics Error:", {
      message: error.message,
      context: context || {},
      timestamp: new Date().toISOString(),
    });

    this.errorCount += 1;
    if (this.errorCount > this.MAX_ERRORS) {
      console.warn("Too many analytics errors, disabling further tracking");
      // Disable analytics by setting initialized to false
      this.isInitialized = false;
    }
  }

  /**
   * Stop session replay
   */
  stop() {
    const openReplay = this.getOpenReplay();
    if (!this.isInitialized || !openReplay) return;

    try {
      openReplay.stop();
    } catch (error) {
      console.warn("Failed to stop session replay:", error);
    }
  }

  /** @internal Test helper — reset singleton flags between Vitest cases. */
  resetForTests(): void {
    this.isInitialized = false;
    this.currentUser = null;
    this.errorCount = 0;
  }
}

export const analytics = new AnalyticsTracker();

/** @internal Test helper */
export function resetAnalyticsForTests(): void {
  analytics.resetForTests();
}

// Initialize analytics when the module is loaded
if (globalThis.window !== undefined) {
  analytics.init();
}
